import { transformDestinationEvent } from '@eventbrite/event-renderer';
import { useIsFetching, useQuery } from '@tanstack/react-query';
import React from 'react';
import {
    EVENT_VERTICALS,
    POI_VERTICALS,
    TTD_VERTICALS,
} from '../components/CategoryFilterBar/utils/eventFormatting';
import { TopActivitiesProps } from '../containers/TopActivities';
import { useSearchContext } from '../context/search';
import { EventData } from '../pages/point-of-interest/containers/CategoryCarousel/CategoryCarousel';
import { PageType } from '../types/AppProps';
import { EventsQueryKey, getQueryKey, search } from './searchUtils';
import {
    BaseEventsQueryOptions,
    Location,
    OrganicEventSearchApiResponse,
    OrganicEventSearchFiltersApiPayload,
    SearchLocationResponse,
} from './types';

export type CategoryCarouselData = {
    category_events: {
        [key: string]: EventData;
    };
    topActivities?: TopActivitiesProps;
    carouselCount: number;
};

const transformResponse = (
    data: OrganicEventSearchApiResponse,
    pageType: PageType | 'search' | '',
): CategoryCarouselData => {
    const VERTICAL = pageType === 'POI' ? POI_VERTICALS : TTD_VERTICALS;
    const events = [
        ...(data.events?.results ?? []),
        ...(data.events?.promoted_results ?? []),
    ];
    const topActivities: TopActivitiesProps = {
        top_events: [],
        num_online_events: 0,
        num_physical_events: 0,
    };

    const categoryEventsObj: CategoryCarouselData['category_events'] =
        VERTICAL.reduce((categoryEvents: any, { vertical, title }) => {
            categoryEvents[vertical] = {
                category: title,
                events: [],
            };

            return categoryEvents;
        }, {});

    events.forEach((event) => {
        const transformedEvent = transformDestinationEvent(event);
        let verticalFound = false;

        VERTICAL.forEach(({ eventChecker, vertical }) => {
            if (eventChecker(transformedEvent)) {
                categoryEventsObj[vertical].events.push(event);
                verticalFound = true;
            }
        });

        if (
            !verticalFound &&
            pageType === 'TTD' &&
            topActivities.top_events.length < 8
        ) {
            topActivities.top_events.push(event);

            if (transformedEvent.isOnlineEvent)
                topActivities.num_online_events += 1;
            else topActivities.num_physical_events += 1;
        } else if (
            !verticalFound &&
            pageType === 'POI' &&
            categoryEventsObj[EVENT_VERTICALS.TOP].events.length < 8
        ) {
            categoryEventsObj[EVENT_VERTICALS.TOP].events.push(event);
        }
    });

    const carouselCount =
        Object.values(categoryEventsObj).filter(
            ({ events }) => events.length > 0,
        ).length ?? 1;

    if (pageType === 'TTD') {
        return {
            category_events: categoryEventsObj,
            topActivities,
            carouselCount,
        };
    }

    return { category_events: categoryEventsObj, carouselCount };
};

export function getSearchLocation(location: Location): SearchLocationResponse {
    const { placeId, isOnline, longitude, latitude } = location;
    let searchOptions = {};

    if (isOnline) {
        searchOptions = {
            online_events_only: true,
        };
    } else {
        searchOptions = {
            point_radius: placeId
                ? undefined
                : {
                      latitude,
                      longitude,
                      radius: '3.5mi',
                  },
            online_events_only: false,
            places: placeId ? [placeId] : undefined,
        };
    }

    return searchOptions as SearchLocationResponse;
}

export function useBaseSearchQuery(
    queryOverload: OrganicEventSearchFiltersApiPayload,
    location: Location,
): OrganicEventSearchFiltersApiPayload {
    const { query } = useSearchContext();
    const searchOptions = getSearchLocation(location);
    return {
        ...query,
        ...searchOptions,
        ...queryOverload,
    };
}

export const useBaseEventsQuery = <ReturnType>(
    selector: (data: OrganicEventSearchApiResponse) => ReturnType,
    queryOverload: OrganicEventSearchFiltersApiPayload,
    queryOptions: BaseEventsQueryOptions,
) => {
    const overloadedQuery = useBaseSearchQuery(
        queryOverload,
        queryOptions?.location,
    );
    const querykey = getQueryKey(queryOverload);

    //determines if the query should automatically run based
    //on if there is a fetch already in progress
    const totalEventQueriesFetching = useIsFetching(querykey.slice(0, 3));

    const shouldEnable = React.useRef(false);
    if (!shouldEnable.current) {
        shouldEnable.current = totalEventQueriesFetching === 0;
    } else {
        shouldEnable.current = true;
    }

    const enabled =
        shouldEnable.current &&
        (queryOptions.enabled === undefined ? true : !!queryOptions.enabled);

    const response = useQuery<
        OrganicEventSearchApiResponse,
        Error,
        ReturnType,
        EventsQueryKey
    >(querykey, async () => await search(overloadedQuery), {
        //React query is complaining that the selector
        //is the provided util or undefined as opposed to
        //a function definition of `unknown`. Ignoring for now
        //eslint-disable-next-line
        //@ts-ignore
        select: selector,
        ...queryOptions,
        enabled,
    });

    return response;
};

export const useQuerySearchService = (options: BaseEventsQueryOptions) =>
    useBaseEventsQuery(
        (data) => transformResponse(data, options.queryOverload.page_type),
        options.queryOverload,
        options,
    );
